import type { Job, JobStatus, JobUsage } from '@/entities/job';
import { getDB, type BrandGeneratorDB } from '@/shared/storage';

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function sumOptional(values: readonly (number | undefined)[]): number | undefined {
  let any = false;
  let total = 0;
  for (const v of values) {
    if (v !== undefined) {
      any = true;
      total += v;
    }
  }
  return any ? total : undefined;
}

function deriveCompositeStatus(children: readonly Job[]): JobStatus {
  if (children.length === 0) return 'pending';
  if (children.every((c) => c.status === 'succeeded')) return 'succeeded';
  if (children.some((c) => c.status === 'running' || c.status === 'pending')) return 'running';
  if (children.some((c) => c.status === 'failed')) return 'failed';
  if (children.some((c) => c.status === 'interrupted')) return 'interrupted';
  if (children.some((c) => c.status === 'canceled')) return 'canceled';
  return 'pending';
}

/**
 * Recomputes a composite job's status, actual cost, and usage from its
 * direct children. Cascades upward if the composite itself has a parent.
 */
export async function recomputeComposite(
  parentId: string,
  db: BrandGeneratorDB = getDB(),
): Promise<void> {
  const parent = await db.jobs.get(parentId);
  if (!parent || parent.kind !== 'composite') return;

  const children = await db.jobs.where('parentJobId').equals(parentId).toArray();
  const status = deriveCompositeStatus(children);
  const actualCostUsd = round4(children.reduce((s, c) => s + c.actualCostUsd, 0));

  const usage: JobUsage = {};
  const inT = sumOptional(children.map((c) => c.usage.inputTokens));
  const outT = sumOptional(children.map((c) => c.usage.outputTokens));
  const sec = sumOptional(children.map((c) => c.usage.predictTimeSec));
  if (inT !== undefined) usage.inputTokens = inT;
  if (outT !== undefined) usage.outputTokens = outT;
  if (sec !== undefined) usage.predictTimeSec = sec;

  const patch: Partial<Job> = { status, actualCostUsd, usage };
  if (status === 'running' && !parent.startedAt) patch.startedAt = Date.now();
  if (
    (status === 'succeeded' || status === 'failed' || status === 'canceled') &&
    !parent.completedAt
  ) {
    patch.completedAt = Date.now();
  }

  await db.jobs.update(parentId, patch);

  if (parent.parentJobId) await recomputeComposite(parent.parentJobId, db);
}
